import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import axiosInstance from "../../utils/axiosInstance";
import { ORGANISATION_ROLES } from "../../utils/roles";
import FormModal from "./FormModal";

/**
 * Unified add-member modal. Posts to organisation members API.
 */
export default function AddMemberModal({
  isOpen,
  onClose,
  onAdded,
  organisationId,
  showLanguageFields = false,
}) {
  const [submissionError, setSubmissionError] = useState("");

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: "",
      firstname: "",
      lastname: "",
      password: "",
      role: ORGANISATION_ROLES.USER,
      hourlyCost: "",
      hourlyRate: "",
    },
    validationSchema: Yup.object({
      email: Yup.string().email("Invalid email").required("Email is required"),
      firstname: Yup.string(),
      lastname: Yup.string(),
      password: Yup.string().min(6, "At least 6 characters"),
      hourlyCost: Yup.number().min(0).nullable(),
      hourlyRate: Yup.number().min(0).nullable(),
    }),
    onSubmit: async (values) => {
      try {
        setSubmissionError("");
        const payload = {
          email: values.email.toLowerCase().trim(),
          firstname: values.firstname,
          lastname: values.lastname,
          role: values.role,
        };
        if (values.password) payload.password = values.password;
        if (values.hourlyCost !== "") {
          payload.hourlyCost = parseFloat(values.hourlyCost) || null;
        }
        if (values.hourlyRate !== "") {
          payload.hourlyRate = parseFloat(values.hourlyRate) || null;
        }

        const endpoint = organisationId
          ? `/admin/organisations/${organisationId}/members`
          : "/admin/my-company/members";

        await axiosInstance.post(endpoint, payload);
        formik.resetForm();
        onAdded?.();
        onClose();
      } catch (error) {
        setSubmissionError(
          error.response?.data?.message || "Failed to add member."
        );
      }
    },
  });

  return (
    <FormModal isOpen={isOpen} onClose={onClose} title="Add Member">
      <p className="text-sm text-gray-600 mb-4">
        Adds a user to this department. If the email already exists, only a
        membership is created.
      </p>
      <form onSubmit={formik.handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Email</label>
          <input
            name="email"
            type="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="w-full border rounded px-3 py-2"
          />
          {formik.touched.email && formik.errors.email && (
            <div className="text-red-500 text-sm">{formik.errors.email}</div>
          )}
        </div>

        <div className="flex gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700">First Name</label>
            <input
              name="firstname"
              value={formik.values.firstname}
              onChange={formik.handleChange}
              className="w-full border rounded px-3 py-2"
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700">Last Name</label>
            <input
              name="lastname"
              value={formik.values.lastname}
              onChange={formik.handleChange}
              className="w-full border rounded px-3 py-2"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Password (new users only)
          </label>
          <input
            name="password"
            type="password"
            value={formik.values.password}
            onChange={formik.handleChange}
            className="w-full border rounded px-3 py-2"
            placeholder="Required when creating a new account"
          />
          {formik.touched.password && formik.errors.password && (
            <div className="text-red-500 text-sm">{formik.errors.password}</div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Role</label>
          <select
            name="role"
            value={formik.values.role}
            onChange={formik.handleChange}
            className="w-full border rounded px-3 py-2"
          >
            <option value={ORGANISATION_ROLES.USER}>User</option>
            <option value={ORGANISATION_ROLES.ORGANISATION_ADMIN}>Department Admin</option>
          </select>
        </div>

        <div className="flex gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700">Hourly Cost (SEK)</label>
            <input
              name="hourlyCost"
              type="number"
              step="0.01"
              min="0"
              value={formik.values.hourlyCost}
              onChange={formik.handleChange}
              className="w-full border rounded px-3 py-2"
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700">Hourly Rate (SEK)</label>
            <input
              name="hourlyRate"
              type="number"
              step="0.01"
              min="0"
              value={formik.values.hourlyRate}
              onChange={formik.handleChange}
              className="w-full border rounded px-3 py-2"
            />
          </div>
        </div>

        {submissionError && <div className="text-red-500 text-sm">{submissionError}</div>}

        <div className="flex justify-end space-x-2">
          <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-300 rounded">
            Cancel
          </button>
          <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded">
            Add Member
          </button>
        </div>
      </form>
    </FormModal>
  );
}
